import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from './Dashboard';
import { API } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, Copy, CheckCircle } from 'lucide-react';

const UpcomingReservations = () => {
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [newUpcoming, setNewUpcoming] = useState({
    guest_name: '',
    booking_platform: '',
    check_in_date: ''
  });

  useEffect(() => {
    loadUpcoming();
  }, []);

  const loadUpcoming = async () => {
    try {
      const res = await axios.get(`${API}/upcoming-reservations`);
      setUpcoming(res.data);
    } catch (error) {
      toast.error('Failed to load upcoming reservations');
    } finally {
      setLoading(false);
    }
  };

  const createUpcoming = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/upcoming-reservations`, newUpcoming);
      toast.success('Upcoming reservation created!');
      setShowModal(false);
      setNewUpcoming({ guest_name: '', booking_platform: '', check_in_date: '' });
      loadUpcoming();
    } catch (error) {
      toast.error('Failed to create upcoming reservation');
    }
  };

  const deleteUpcoming = async (id) => {
    if (!window.confirm('Delete this upcoming reservation?')) return;
    try {
      await axios.delete(`${API}/upcoming-reservations/${id}`);
      toast.success('Upcoming reservation deleted');
      loadUpcoming();
    } catch (error) {
      toast.error('Failed to delete upcoming reservation');
    }
  };

  const copyCheckInLink = (token) => {
    const link = `${window.location.origin}/?token=${token}`;
    navigator.clipboard.writeText(link);
    setCopiedId(token);
    toast.success('Check-in link copied!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-3xl font-bold text-gray-900">Upcoming Reservations</h2>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-4 h-4" />
            <span>New Upcoming</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {upcoming.map((item) => (
            <div key={item.id} className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{item.guest_name}</h3>
                  <p className="text-sm text-gray-500">{item.booking_platform}</p>
                </div>
                <button
                  onClick={() => deleteUpcoming(item.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>

              <p className="text-gray-700 mb-4">
                <span className="font-medium">Check-in:</span> {item.check_in_date}
              </p>

              <button
                onClick={() => copyCheckInLink(item.check_in_token)}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg transition ${
                  copiedId === item.check_in_token
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {copiedId === item.check_in_token ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy Check-in Link</span>
                  </>
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Create Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
              <h3 className="text-2xl font-bold mb-4">New Upcoming Reservation</h3>
              <form onSubmit={createUpcoming} className="space-y-4">
                <input
                  type="text"
                  placeholder="Guest Name"
                  required
                  value={newUpcoming.guest_name}
                  onChange={(e) => setNewUpcoming({ ...newUpcoming, guest_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
                <input
                  type="text"
                  placeholder="Booking Platform (e.g., Booking.com)"
                  required
                  value={newUpcoming.booking_platform}
                  onChange={(e) => setNewUpcoming({ ...newUpcoming, booking_platform: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
                <input
                  type="date"
                  required
                  value={newUpcoming.check_in_date}
                  onChange={(e) => setNewUpcoming({ ...newUpcoming, check_in_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-600 text-white py-2 rounded hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UpcomingReservations;
